import { create } from 'zustand';
import type { ThemePreference } from './theme';

export type ChatPosition = 'right' | 'footer' | 'detached';

export type AgentActivity = {
  correlationId: string;
  description: string;
  timestamp: string;
};

/** Minimum chat size when docked. */
export const CHAT_MIN_WIDTH = 280;
export const CHAT_MIN_HEIGHT = 240;
export const CHAT_DEFAULT_WIDTH = 360;
export const CHAT_DEFAULT_HEIGHT = 320;

const LS_KEY = 'trms_workbench_app_prefs_v1';

type PersistedPrefs = Partial<{
  leftNavCollapsed: boolean;
  chatCollapsed: boolean;
  chatPosition: ChatPosition;
  chatWidth: number;
  chatHeight: number;
  theme: ThemePreference;
  locale: string;
}>;

function readPersisted(): PersistedPrefs {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as PersistedPrefs;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writePersisted(prefs: PersistedPrefs): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LS_KEY, JSON.stringify(prefs));
  } catch {
    /* quota / privacy-mode — ignore */
  }
}

type AppState = {
  leftNavCollapsed: boolean;
  chatCollapsed: boolean;
  chatPosition: ChatPosition;
  chatWidth: number;
  chatHeight: number;
  theme: ThemePreference;
  locale: string;
  lastAgentAction: AgentActivity | null;
  commandPaletteOpen: boolean;
  layoutSaveDialogOpen: boolean;
  layoutLoadDialogOpen: boolean;
  layoutSuggestedName: string;

  toggleLeftNav: () => void;
  toggleChat: () => void;
  setChatPosition: (p: ChatPosition) => void;
  setChatWidth: (w: number) => void;
  setChatHeight: (h: number) => void;
  setTheme: (t: ThemePreference) => void;
  setLocale: (l: string) => void;
  setLastAgentAction: (a: AgentActivity | null) => void;
  setCommandPaletteOpen: (v: boolean) => void;
  openLayoutSaveDialog: (initialName?: string) => void;
  setLayoutSaveDialogOpen: (v: boolean) => void;
  openLayoutLoadDialog: () => void;
  setLayoutLoadDialogOpen: (v: boolean) => void;
  /** Re-hydrate from backend prefs. */
  hydrate: (patch: PersistedPrefs) => void;
};

function snapshot(s: AppState): PersistedPrefs {
  return {
    leftNavCollapsed: s.leftNavCollapsed,
    chatCollapsed: s.chatCollapsed,
    chatPosition: s.chatPosition,
    chatWidth: s.chatWidth,
    chatHeight: s.chatHeight,
    theme: s.theme,
    locale: s.locale,
  };
}

const persisted = readPersisted();

export const useAppStore = create<AppState>((set, get) => ({
  leftNavCollapsed: persisted.leftNavCollapsed ?? false,
  chatCollapsed: persisted.chatCollapsed ?? false,
  chatPosition: persisted.chatPosition ?? 'right',
  chatWidth: persisted.chatWidth ?? CHAT_DEFAULT_WIDTH,
  chatHeight: persisted.chatHeight ?? CHAT_DEFAULT_HEIGHT,
  theme: persisted.theme ?? 'system',
  locale: persisted.locale ?? 'en',
  lastAgentAction: null,
  commandPaletteOpen: false,
  layoutSaveDialogOpen: false,
  layoutLoadDialogOpen: false,
  layoutSuggestedName: '',

  toggleLeftNav: () =>
    set((s) => {
      const next = !s.leftNavCollapsed;
      writePersisted({ ...snapshot(s), leftNavCollapsed: next });
      return { leftNavCollapsed: next };
    }),
  toggleChat: () =>
    set((s) => {
      const next = !s.chatCollapsed;
      writePersisted({ ...snapshot(s), chatCollapsed: next });
      return { chatCollapsed: next };
    }),
  setChatPosition: (p) => {
    writePersisted({ ...snapshot(get()), chatPosition: p });
    set({ chatPosition: p });
  },
  setChatWidth: (w) => {
    const clamped = Math.max(CHAT_MIN_WIDTH, Math.round(w));
    writePersisted({ ...snapshot(get()), chatWidth: clamped });
    set({ chatWidth: clamped });
  },
  setChatHeight: (h) => {
    const clamped = Math.max(CHAT_MIN_HEIGHT, Math.round(h));
    writePersisted({ ...snapshot(get()), chatHeight: clamped });
    set({ chatHeight: clamped });
  },
  setTheme: (t) => {
    writePersisted({ ...snapshot(get()), theme: t });
    set({ theme: t });
  },
  setLocale: (l) => {
    writePersisted({ ...snapshot(get()), locale: l });
    set({ locale: l });
  },
  setLastAgentAction: (a) => set({ lastAgentAction: a }),
  setCommandPaletteOpen: (v) => set({ commandPaletteOpen: v }),
  openLayoutSaveDialog: (initialName = '') =>
    set({ layoutSaveDialogOpen: true, layoutSuggestedName: initialName }),
  setLayoutSaveDialogOpen: (v) =>
    set((s) => ({
      layoutSaveDialogOpen: v,
      layoutSuggestedName: v ? s.layoutSuggestedName : '',
    })),
  openLayoutLoadDialog: () => set({ layoutLoadDialogOpen: true }),
  setLayoutLoadDialogOpen: (v) => set({ layoutLoadDialogOpen: v }),
  hydrate: (patch) => {
    set((s) => {
      const merged = { ...s, ...patch };
      writePersisted(snapshot(merged));
      return merged;
    });
  },
}));
